import {
  BundleJson,
  CourierJson,
  Instruction,
  InstructionType,
  TravelMode,
} from '../types/bundleTypes';
import {
  BundleDeliveryStatus,
  BundlesInfoStatus,
  InstructionGroup,
  MonitorBundlesWithOrders,
  MonitorLiveViewModes,
  MonitorStorageActions,
  MoveOrdersModal,
  OrderInstructionsByBundles,
  Types,
} from '../pages/monitor/MonitorLive/Monitor.types';
import {
  OrderInstruction,
  toOrderInstructions,
} from '../components/maps/BundleMap';
import { fetchAllCouriers } from '../fetch/fetchUsers';
import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { Order, OrderMonitor, Region } from '../types/orderTypes';
import {
  fetchAllMonitorBundlesWithOrders,
  fetchSelectedMonitorBundlesWithOrders,
  findNextInstruction,
} from '../pages/bundles/components/BundleUtils';
import { subscribeToChannel, unsubscribeFromChannel } from './pusher';
import {
  bundleChannelSubscription,
  bundleEventName,
} from '../const/pusherChannelEventConsts';
import dayjs, { Dayjs } from 'dayjs';
import {
  calculateEtaWithAlgorithm,
  pickupOnTop,
  publishDraft,
  reorderDraftNew,
  splitBundleAndCreateNew,
} from '../pages/oldPlanner/utils/bundleDraft';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { onTriggerMoveOrdersToSelectedBundle } from '../pages/oldPlanner/planner/MovingOrdersToBundleModalContent';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import _ from 'lodash';
import { TooltipComponent } from '../ReusableComponents/Tooltip/TooltipComponent';
import { SwitchComponent } from '../ReusableComponents/Switch/SwitchComponent';
import { useDeepCompareEffect } from 'use-deep-compare';
import { fetchOrdersForMonitor } from '../fetch/orderFetch';

enum PlateId {
  COURIER = 'COURIER',
  PRODUCT = 'PRODUCT',
  SHELF = 'SHELF',
  'PICK-UPS' = 'PICK UPS',
  ZONES = 'ZONES',
  BUNDLES = 'BUNDLES',
  DELIVERIES = 'DELIVERIES',
  'LAST-ETA' = 'LAST ETA',
  DEVIATIONS = 'DEVIATIONS',
}

export const useCombinedEffects = (
  fetchAllMonitorBundleDataCallback: () => Promise<void>,
  bundles: BundleJson[],
  orders: OrderInstructionsByBundles,
  subscribeToPusherCallback: (currentBundlesId: string[]) => void,
  memoizedCurrentBundlesId: string[],
  region: Region | null
): void => {
  useEffect(() => {
    fetchAllMonitorBundleDataCallback()
      .then(() => console.log('resolved'))
      .catch(error => console.log(error));
  }, [fetchAllMonitorBundleDataCallback]);

  const referenceToOrdersLength = useRef(
    orders ? Object.keys(orders).length : 0
  );

  useDeepCompareEffect(() => {
    referenceToOrdersLength.current = orders ? Object.keys(orders).length : 0;
  }, [orders]);

  useEffect(() => {
    if (memoizedCurrentBundlesId.length) {
      subscribeToPusherCallback(memoizedCurrentBundlesId);
    }
    return function cleanup() {
      unsubscribeFromChannel(bundleChannelSubscription);
    };
  }, [memoizedCurrentBundlesId, subscribeToPusherCallback, region]);
};

export function checkStatusOfDelivery(
  bundle: BundleJson
): BundleDeliveryStatus | undefined {
  const firstDelivery = bundle.instructions.filter(
    instruction => instruction.type === InstructionType.Delivery
  )[0];
  const now = dayjs();
  if (
    !firstDelivery ||
    !firstDelivery.eta?.isValid() ||
    now.diff(firstDelivery.eta, 'minute') < -20
  ) {
    return 'not-started';
  }
  const nextDelivery = findNextInstruction(bundle, InstructionType.Delivery);
  if (bundle.status === 'COMPLETED') {
    return 'finished';
  } else if (nextDelivery && nextDelivery.eta) {
    const diff = nextDelivery.eta.diff(now, 'minute');
    return diff <= -1 ? 'delayed' : 'on-time';
  }
}

export function serializeModalData(
  bundleId: string,
  bundlesPage: BundleJson[],
  orders: OrderInstructionsByBundles,
  couriers: CourierJson[]
): {
  currentBundle: BundleJson | undefined;
  currentOrders: OrderInstruction[];
  courier: CourierJson | undefined;
  courierId: number | undefined;
} {
  const currentBundle = bundlesPage.find(bundle => bundle.id === bundleId);
  const currentOrders = orders[bundleId];
  const courier = couriers.find(
    courierObject =>
      courierObject.id.toString() === currentBundle?.assignedCourierId
  );
  const courierId =
    (currentBundle?.assignedCourierId && +currentBundle.assignedCourierId) ||
    undefined;
  return { currentBundle, currentOrders, courier, courierId };
}

function performStringSortByType(
  bundleA: BundleJson,
  bundleB: BundleJson,
  a: string | undefined,
  b: string | undefined,
  sortType: string
): number {
  if (sortType === 'asc') {
    const compareResult = (a || '').localeCompare(b || '', undefined, {
      numeric: true,
    });
    return compareResult === 0 ? +bundleA.id - +bundleB.id : compareResult;
  } else {
    const compareResult = (b || '').localeCompare(a || '', undefined, {
      numeric: true,
    });
    return compareResult === 0 ? +bundleB.id - +bundleA.id : compareResult;
  }
}

export function sortBundlesByKeyOrDefault(
  a: BundleJson,
  b: BundleJson,
  sortKey: string,
  sortType: string,
  orders: OrderInstructionsByBundles
): number {
  let bundleA: number, bundleB: number;

  const getBundleLength = (id: string) =>
    Array.from(
      new Set(
        orders[id]
          .filter(order => order.order.openCourierDeviation)
          .map(order => order.order.id)
      )
    )?.length || 0;

  switch (sortKey.toLowerCase()) {
    case 'departure':
      const departureA =
        a.instructions[0]?.eta && a.instructions[0]?.eta?.isValid()
          ? a.instructions[0].eta.format('HH:mm')
          : '';
      const departureB =
        b.instructions[0]?.eta && b.instructions[0]?.eta?.isValid()
          ? b.instructions[0].eta.format('HH:mm')
          : '';
      return performStringSortByType(a, b, departureA, departureB, sortType);
    case 'shelf':
      return performStringSortByType(a, b, a.shelfId, b.shelfId, sortType);
    case 'temperature':
      const isTempOrderInBundleA = orders[a.id].some(
        orderInstruction => orderInstruction.order.temperatureControlled
      );
      const isTempOrderInBundleB = orders[b.id].some(
        orderInstruction => orderInstruction.order.temperatureControlled
      );
      if (isTempOrderInBundleA && isTempOrderInBundleB) {
        return performStringSortByType(a, b, a.shelfId, b.shelfId, sortType);
      }
      return sortType === 'asc'
        ? isTempOrderInBundleA
          ? -1
          : 1
        : isTempOrderInBundleB
        ? -1
        : 1;
    case 'zones':
      return performStringSortByType(a, b, a.region, b.region, sortType);
    case 'last-eta': {
      const delayedEtaOfBundleA = getBundleEtaDelay(orders[a.id]);
      const delayedEtaOfBundleB = getBundleEtaDelay(orders[b.id]);
      const lastInstructionA: Instruction =
        a.instructions && a.instructions[a.instructions.length - 1];
      const lastInstructionB: Instruction =
        b.instructions && b.instructions[b.instructions.length - 1];
      const aEta =
        lastInstructionA &&
        lastInstructionA.eta &&
        lastInstructionA.eta.isValid()
          ? delayedEtaOfBundleA
            ? lastInstructionA.eta
                .subtract(delayedEtaOfBundleA.delayMinutes, 'minute')
                .format('HH:mm')
            : lastInstructionA.eta.format('HH:mm')
          : '-';
      const bEta =
        lastInstructionB &&
        lastInstructionB.eta &&
        lastInstructionB.eta.isValid()
          ? delayedEtaOfBundleB
            ? lastInstructionB.eta
                .subtract(delayedEtaOfBundleB.delayMinutes, 'minute')
                .format('HH:mm')
            : lastInstructionB.eta.format('HH:mm')
          : '-';
      return performStringSortByType(a, b, aEta, bEta, sortType);
    }
    case 'deviations':
      bundleA = getBundleLength(a.id);
      bundleB = getBundleLength(b.id);
      break;
    case 'courier':
      return performStringSortByType(
        a,
        b,
        a.assignedCourierName,
        b.assignedCourierName,
        sortType
      );
    case 'pick-ups': {
      const pickupsA = a.instructions.filter(
        pickup => pickup.type === InstructionType.Pickup && pickup.completed
      )?.length;
      const totalA = a.instructions.length ? a.instructions.length / 2 : 0;
      const pickupsB = b.instructions.filter(
        pickup => pickup.type === InstructionType.Pickup && pickup.completed
      )?.length;
      const totalB = b.instructions.length ? b.instructions.length / 2 : 0;
      bundleA = totalA ? pickupsA / totalA : 0;
      bundleB = totalB ? pickupsB / totalB : 0;
      break;
    }
    case 'deliveries': {
      const deliveredA = a.instructions.filter(
        delivery =>
          delivery.type === InstructionType.Delivery && delivery.completed
      )?.length;
      const totalA = a.instructions.length ? a.instructions.length / 2 : 0;
      const deliveredB = b.instructions.filter(
        delivery =>
          delivery.type === InstructionType.Delivery && delivery.completed
      )?.length;
      const totalB = b.instructions.length ? b.instructions.length / 2 : 0;
      bundleA = totalA ? deliveredA / totalA : 0;
      bundleB = totalB ? deliveredB / totalB : 0;
      break;
    }
    default:
      bundleA = +a.id;
      bundleB = +b.id;
      break;
  }
  if (sortType === 'asc') {
    return bundleA - bundleB === 0 ? +a.id - +b.id : bundleA - bundleB;
  } else {
    return bundleA - bundleB === 0 ? +b.id - +a.id : bundleB - bundleA;
  }
}

export function sortBundlesByStatus(
  bundles: BundleJson[],
  bundlesInfoStatus: BundlesInfoStatus
): BundleJson[] {
  const copyOfBundles = [...bundles];
  return copyOfBundles.sort((a, b) => {
    const bundleA = sortByLateDeliveries(a.id, bundlesInfoStatus);
    const bundleB = sortByLateDeliveries(b.id, bundlesInfoStatus);
    if (bundleA === bundleB) {
      return +b.id - +a.id;
    }
    return bundleA - bundleB;
  });
}

export function updateBundlesInfoStatusList(
  bundles: BundleJson[],
  currentBundlesInfoStatus: BundlesInfoStatus
): BundlesInfoStatus {
  const copyOfBundlesInfoStatus = { ...currentBundlesInfoStatus };
  if (bundles.length === 1) {
    const updatedBundle = bundles[0];
    copyOfBundlesInfoStatus[updatedBundle.id] = {
      ...copyOfBundlesInfoStatus[updatedBundle.id],
      bundleDeliveryStatus: checkStatusOfDelivery(updatedBundle),
      loading: false,
      draftOrderList: [],
      selectedOrders: [],
    };
  } else {
    bundles.forEach(bundle => {
      copyOfBundlesInfoStatus[bundle.id] = {
        ...copyOfBundlesInfoStatus[bundle.id],
        bundleDeliveryStatus: checkStatusOfDelivery(bundle),
        loading: false,
        draftOrderList: [],
        selectedOrders: [],
      };
    });
  }
  return copyOfBundlesInfoStatus;
}

export function createInitialBundleInfoStatusList(
  bundles: BundleJson[]
): BundlesInfoStatus {
  return bundles.reduce(
    (currentObject, bundle) =>
      Object.assign(currentObject, {
        [bundle.id]: {
          loading: false,
          bundleDeliveryStatus: checkStatusOfDelivery(bundle),
          collapseOpen: false,
          tablePickupsOpen: false,
          view: 'normal',
          selectedOrders: [],
          draftOrderList: [],
        },
      }),
    {}
  );
}

export function sortBundlesBySortSettings(
  bundles: BundleJson[],
  bundlesInfoStatus: BundlesInfoStatus,
  newSortKey: string,
  currentSortKey: string,
  orders: OrderInstructionsByBundles
): { sortedBundles: BundleJson[]; sortType: string } {
  const copyOfBundles = [...bundles];
  const sortBundles = (sortKey: string) =>
    copyOfBundles.sort((a, b) =>
      sortBundlesByKeyOrDefault(a, b, newSortKey, sortKey, orders)
    );

  switch (newSortKey) {
    case '': {
      const sortType = '';
      const sortedBundles = sortBundles(sortType);
      return { sortedBundles, sortType };
    }
    case currentSortKey: {
      const sortType = 'desc';
      const sortedBundles = sortBundles(sortType);
      return { sortedBundles, sortType };
    }
    default: {
      const sortType = 'asc';
      const sortedBundles = sortBundles(sortType);
      return { sortedBundles, sortType };
    }
  }
}

function buildOrdersByBundles(
  bundles: BundleJson[],
  orders: { [bundleId: string]: OrderMonitor[] }
): OrderInstructionsByBundles {
  const ordersByBundles: OrderInstructionsByBundles = {};
  bundles.forEach(bundle => {
    ordersByBundles[bundle.id] = toOrderInstructions(bundle, orders[bundle.id]);
  });
  return ordersByBundles;
}

function createOrderInstruction(
  bundles: BundleJson[],
  orders: { [bundleId: string]: OrderMonitor[] }
): OrderInstructionsByBundles {
  return buildOrdersByBundles(bundles, orders);
}

export async function updateSelectedBundleOrBundles(
  bundleIds: string[]
): Promise<{
  fetchedBundles: BundleJson[];
  fetchedOrders: OrderInstructionsByBundles;
} | null> {
  const { bundles, orders } = await fetchSelectedMonitorBundlesWithOrders(
    bundleIds
  );
  const ordersByBundles = createOrderInstruction(bundles, orders);
  return { fetchedBundles: bundles, fetchedOrders: ordersByBundles };
}

export async function addSelectedBundleOrBundles(
  bundleIds: string[],
  dispatch: Dispatch<MonitorStorageActions>,
  region: Region | null
): Promise<void> {
  let filteredBundles: BundleJson[] | undefined;
  const { bundles, orders } = await fetchSelectedMonitorBundlesWithOrders(
    bundleIds
  );
  if (region) {
    filteredBundles = bundles.filter(bundle => bundle.region === region);
  }
  const ordersByBundles = createOrderInstruction(
    filteredBundles || bundles,
    orders
  );
  if ((region && filteredBundles?.length) || !region) {
    dispatch({
      type: Types.AddNewBundle,
      payload: { bundles, orders: ordersByBundles },
    });
  }
}

const throttledDispatch = _.throttle(
  (
    bundleIds: string[],
    dispatch: Dispatch<MonitorStorageActions>,
    clearIdsArray: (bundleIds: string[]) => void,
    updateSelectedBundleOrBundlesCallback: (
      bundleIds: string[]
    ) => Promise<void>
  ): void => {
    if (bundleIds.length === 1) {
      const [bundleId] = bundleIds;
      dispatch({
        type: Types.SetLoadingForBundle,
        payload: { bundleId, bundlesLoadingStatus: true },
      });
      updateSelectedBundleOrBundlesCallback(bundleIds)
        .then(() => console.log('update done'))
        .catch(error => console.log(error));
      clearIdsArray(bundleIds);
    } else {
      dispatch({
        type: Types.SetLoadingForBundles,
        payload: { bundleIds, bundlesLoadingStatus: true },
      });
      updateSelectedBundleOrBundlesCallback(bundleIds)
        .then(() => console.log('update done'))
        .catch(error => console.log(error));
      clearIdsArray(bundleIds);
    }
  },
  1500,
  { leading: false }
);

export function subscribeToPusherForBundleInfo(
  dispatch: Dispatch<MonitorStorageActions>,
  updateSelectedBundleOrBundlesCallback: (bundleIds: string[]) => Promise<void>,
  currentBundlesId: string[],
  region: Region | null
): void {
  let bundleIdsToUpdate: string[] = [];
  subscribeToChannel(bundleChannelSubscription, bundleEventName, async data => {
    const {
      bundleId,
      bundlesId,
      message,
    }: { bundleId: string; bundlesId: string[]; message: string } = data;
    console.log('bundle info ', bundleId, bundlesId, message);
    if (message === 'Published new bundles') {
      await addSelectedBundleOrBundles(bundlesId, dispatch, region);
    } else {
      const clearIds = (updatedIds: string[]) => {
        if (bundleIdsToUpdate.length === updatedIds.length) {
          bundleIdsToUpdate = [];
        } else {
          bundleIdsToUpdate = bundleIdsToUpdate.filter(
            id => !updatedIds.includes(id)
          );
        }
      };
      if (currentBundlesId.includes(bundleId)) {
        bundleIdsToUpdate.push(bundleId);
        throttledDispatch(
          bundleIdsToUpdate,
          dispatch,
          clearIds,
          updateSelectedBundleOrBundlesCallback
        );
      }
    }
  });
}

export async function fetchAllMonitorBundleData(
  dispatch: Dispatch<MonitorStorageActions>,
  region: Region | null
): Promise<void> {
  const { bundles, orders }: MonitorBundlesWithOrders =
    await fetchAllMonitorBundlesWithOrders(region);
  const couriers: CourierJson[] = await fetchAllCouriers();
  const initialBundleInfoStatus = createInitialBundleInfoStatusList(bundles);

  dispatch({
    type: Types.StoreAllFetchedData,
    payload: {
      bundles,
      orders: buildOrdersByBundles(bundles, orders),
      couriers,
      bundlesInfoStatus: initialBundleInfoStatus,
      loading: false,
      autoSort: false,
    },
  });
}

export function findPickupInstructionsGroup(
  instructions: OrderInstruction[]
): InstructionGroup | null {
  let group: InstructionGroup | null = null;
  for (let i = 0, l = instructions.length; i < l; i++) {
    const instr = instructions[i].instruction;
    const order = instructions[i].order;
    if (!order) {
      break;
    }
    const address = order.originAddress.fullAddressString.replace(
      /\s\s+/g,
      ' '
    );

    if (group === null) {
      group = {
        address,
        instruction: instr,
        index: i,
        count: 1,
      };
    } else if (instr.type === InstructionType.Pickup) {
      group.count++;
    } else {
      break;
    }
  }
  return group &&
    group.count > 1 &&
    group.instruction.type === InstructionType.Pickup
    ? group
    : null;
}

export function getPin(
  order: Order | OrderMonitor,
  instruction: Instruction
): string {
  switch (instruction.type) {
    case InstructionType.Delivery:
      return order.confirmationPin;
    case InstructionType.Pickup:
      return order.pickupPin;
    default:
      return '';
  }
}

export function getInstructionTime(
  order: Order | OrderMonitor,
  instruction: Instruction
): dayjs.Dayjs | null {
  const type = instruction.type;
  let timeVal: string | undefined;
  if (type === InstructionType.Delivery) {
    timeVal = order.deliveredAt;
  } else if (type === InstructionType.Pickup) {
    timeVal = order.pickedAt;
  }
  return timeVal ? dayjs(timeVal) : null;
}

export function getInstructionAddress(
  instruction: Instruction,
  order: Order | OrderMonitor
): string {
  return instruction.type === 'PICKUP'
    ? `${order.senderName || ''}, ${order.originAddress.postalCode} ${
        order.originAddress.city
      }`
    : `${order.destinationAddress?.fullAddressString}`;
}

export function getDiffTime(
  instruction: Instruction,
  order: Order | OrderMonitor,
  instructionTime: dayjs.Dayjs | null
): string | number {
  return instruction.eta?.isValid() && order && instructionTime !== null
    ? dayjs(instruction.eta).diff(instructionTime, 'minute')
    : '-';
}

export function formatTime(time: string | Dayjs): string {
  if (typeof time === 'string') {
    return dayjs(time).format('HH:mm');
  } else if (time instanceof dayjs) {
    return time.isValid() ? time.format('HH:mm') : 'No ETA';
  }
  return 'No ETA';
}

export function getBundleEtaDelay(
  instructions: OrderInstruction[]
): { delayMinutes: number; lastDeliveredIdx: number } | null {
  let latestDeliveredIdx = -1;
  let latestDeliveredTime: Dayjs | null = null;
  instructions.forEach((instr, i) => {
    if (
      instr?.instruction.type === InstructionType.Delivery &&
      instr?.order?.deliveredAt
    ) {
      const deliveredAt = dayjs(instr?.order?.deliveredAt);
      if (
        !instr?.order?.openCourierDeviation &&
        (!latestDeliveredTime ||
          (latestDeliveredTime.isBefore(deliveredAt) &&
            instr?.order?.courierDeviations?.length === 0))
      ) {
        latestDeliveredTime = deliveredAt;
        latestDeliveredIdx = i;
      }
    }
  });
  if (latestDeliveredIdx > -1) {
    const delayMinutes = getDiffTime(
      instructions[latestDeliveredIdx].instruction,
      instructions[latestDeliveredIdx].order,
      latestDeliveredTime
    ) as number;
    return {
      delayMinutes,
      lastDeliveredIdx: latestDeliveredIdx,
    };
  }
  return null;
}

export function calculateEtaFromAlgorithm(
  dispatch: Dispatch<MonitorStorageActions>,
  bundleId: string,
  travelMode: TravelMode,
  timePerStop: number,
  updateSelectedBundleOrBundlesCallback: (bundleIds: string[]) => Promise<void>
): void {
  calculateEtaWithAlgorithm(
    bundleId,
    travelMode,
    timePerStop,
    async () => {
      await updateSelectedBundleOrBundlesCallback([bundleId]);
    },
    response => {
      Swal.fire({
        title: `Something unexpected occurred when calculating ETAs. Error response: ${JSON.stringify(
          response
        )}`,
        icon: 'error',
      })
        .then(() => null)
        .catch(error => console.log(error));
    }
  )
    .then(() => null)
    .catch(error => console.log(error));
}

export function createStrokeColorForProgress(
  currentDeliveryStatus: BundleDeliveryStatus
): null | {
  bar: string;
  root: string;
} {
  switch (currentDeliveryStatus) {
    case 'on-time':
      return { bar: '#10ca2c', root: 'transparent' };
    case 'delayed':
      return { bar: '#dd4912', root: 'transparent' };
    case 'finished':
      return { bar: '#000', root: '#000' };
    default:
      return null;
  }
}

export function useInterval(callback: () => void, delay: number): void {
  const savedCallback = useRef<() => void>();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback?.current?.();
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => {
        clearInterval(id);
      };
    }
  }, [delay]);
}

export function sortByLateDeliveries(
  bundleId: string,
  bundlesInfoStatus: BundlesInfoStatus
): number | undefined {
  if (!bundlesInfoStatus || !bundlesInfoStatus[bundleId]) {
    return 3;
  }
  const { bundleDeliveryStatus = 'delayed' } = bundlesInfoStatus[bundleId];

  switch (bundleDeliveryStatus) {
    case 'delayed':
      return 0;
    case 'on-time':
      return 1;
    case 'not-started':
      return 2;
    case 'finished':
      return 3;
    default:
      return undefined;
  }
}

export async function executeMoveOrders(
  bundleIdMoveFrom: string,
  selectedBundleToMoveOrders: string,
  currentBundle: BundleJson | undefined | null,
  selectedOrders: string[]
): Promise<Response | boolean> {
  return onTriggerMoveOrdersToSelectedBundle(
    bundleIdMoveFrom,
    selectedBundleToMoveOrders,
    null,
    selectedOrders
  );
}

export function renderTitles(
  completedStatusList: boolean,
  sortKey: string,
  sortType: string,
  dispatch: Dispatch<MonitorStorageActions>,
  autoSort: boolean
): JSX.Element[] {
  return Object.keys(PlateId)
    .map(key => {
      const title = (PlateId as Record<string, string>)[key];
      let sortArrow = null;
      if (sortKey === key && sortType === 'asc') {
        sortArrow = <FaArrowUp />;
      } else if (sortKey === key && sortType === 'desc') {
        sortArrow = <FaArrowDown />;
      }
      if (
        key === PlateId.COURIER ||
        key === PlateId.PRODUCT ||
        key === PlateId.SHELF ||
        key === PlateId.BUNDLES ||
        key === PlateId.ZONES ||
        key === PlateId.DELIVERIES ||
        key === 'PICK-UPS' ||
        key === 'LAST-ETA' ||
        key === PlateId.DEVIATIONS
      ) {
        return (
          <TooltipComponent
            key={key}
            className={`header-container tooltip ${key.toLowerCase()}`}
            id={key}
            title={`Sort by ${PlateId[key].toLowerCase()}`}
            placement={key === PlateId.COURIER ? 'top-start' : 'top'}
          >
            <div
              key={key}
              onClick={() =>
                dispatch({
                  type: Types.SetSortByKey,
                  payload: { sortKey: key },
                })
              }
              style={{ cursor: 'pointer' }}
              className={`header-container ${key.toLowerCase()}`}
            >
              <span>
                {title} {sortArrow}
              </span>
            </div>
          </TooltipComponent>
        );
      } else {
        return (
          <div key={key} className={`header-container ${key.toLowerCase()}`}>
            <span>
              {title} {sortArrow}
            </span>
          </div>
        );
      }
    })
    .concat(
      <div className="header-container icons auto-sort" key="auto sort">
        <TooltipComponent
          className="auto-switcher"
          id="auto sort"
          title={`${autoSort ? 'Disable ' : 'Enable '}auto sort`}
        >
          <SwitchComponent
            size="small"
            checked={autoSort}
            id="auto-sort-switcher"
            name="auto-sort-switcher"
            onChange={() =>
              dispatch({
                type: Types.SetAutoSort,
                payload: { autoSort: !autoSort },
              })
            }
          />
        </TooltipComponent>
      </div>
    );
}

export async function triggerMoveAction(
  bundleId: string,
  selectedBundleToMoveOrdersTo: string,
  currentSelectedRows: string[],
  toggleMoveOrdersModal: Dispatch<SetStateAction<MoveOrdersModal>>,
  setSelectedBundleToMoveOrdersTo: Dispatch<SetStateAction<string>>
): Promise<void> {
  const response = await executeMoveOrders(
    bundleId,
    selectedBundleToMoveOrdersTo,
    null,
    currentSelectedRows
  );
  if (typeof response === 'boolean') {
    return;
  }
  if (response) {
    await Swal.fire({
      position: 'center',
      icon: 'success',
      title: `Orders moved to the bundle ${selectedBundleToMoveOrdersTo}`,
      showConfirmButton: false,
      timer: 2000,
    });
    toggleMoveOrdersModal({ isOpen: false });
    setSelectedBundleToMoveOrdersTo('');
  } else {
    await Swal.fire({
      position: 'center',
      icon: 'error',
      title: `Something went wrong. Selected orders didn't move to the bundle ${selectedBundleToMoveOrdersTo}`,
      showConfirmButton: false,
      timer: 3000,
    });
  }
}

export async function recreateBundle(
  selectedOrders: string[],
  region: Region,
  bundleId: string,
  dispatch: Dispatch<MonitorStorageActions>,
  setBundleViewMode: Dispatch<SetStateAction<MonitorLiveViewModes>>,
  setSelectedRows: Dispatch<SetStateAction<string[]>>,
  updateSelectedBundleOrBundlesCallback: (bundleIds: string[]) => Promise<void>
): Promise<void> {
  return splitBundleAndCreateNew(selectedOrders, region, bundleId)
    .then(async createdBundle => {
      setBundleViewMode('normal');
      await updateSelectedBundleOrBundlesCallback([bundleId]);
      let fetchedOrders: OrderMonitor[];
      try {
        fetchedOrders = await fetchOrdersForMonitor(
          createdBundle.instructions.map(instruction => instruction.orderId)
        );
      } catch {
        fetchedOrders = [];
      }
      const orderInstructions = toOrderInstructions(
        createdBundle,
        fetchedOrders
      );
      await reorderDraftNew(
        createdBundle.id,
        pickupOnTop(orderInstructions).map(
          orderInstruction => orderInstruction.instruction
        )
      );
      const bundleShouldBePublished = await askForBundleStatus(
        createdBundle.id
      );
      if (bundleShouldBePublished.value) {
        await publishDraft(createdBundle.id);
      }
    })
    .catch(error => {
      console.log(error);
      dispatch({
        type: Types.SetLoadingForBundle,
        payload: { bundleId, bundlesLoadingStatus: false },
      });
      dispatch({
        type: Types.StoreLastChangesInBundle,
        payload: { bundleId },
      });
    });
}

async function askForBundleStatus(bundleId: string): Promise<SweetAlertResult> {
  return Swal.fire({
    title: 'Set status of created bundle',
    html: `Would you like to set bundle ${bundleId} as <b>Published</b>?`,
    icon: 'info',
    showCancelButton: true,
    focusCancel: true,
    confirmButtonText: 'Yes',
    confirmButtonColor: '#00b4bb',
    cancelButtonText: 'No, leave it as Draft',
    cancelButtonColor: '#55ab6d',
  });
}
